import { useState } from "react";

const ROLE_COLORS = {
  national_manager: "bg-purple-100 text-purple-700",
  regional_manager: "bg-indigo-100 text-indigo-700",
  area_manager: "bg-sky-100 text-sky-700",
  campaign_manager: "bg-amber-100 text-amber-700",
  team_leader: "bg-emerald-100 text-emerald-700",
  fundraiser: "bg-slate-100 text-slate-600",
  client_viewer: "bg-slate-100 text-slate-500"
};

// Flatten tree into { id, name, email, teamRole, directReports, downstreamTotal, activeToday, noSyncToday, depth, hasChildren, collapsedBy }
function flatten(roots, collapsed) {
  const out = [];
  const stack = [];
  for (let i = roots.length - 1; i >= 0; i--) {
    stack.push({ node: roots[i], depth: 0, hiddenBy: null });
  }
  while (stack.length) {
    const { node, depth, hiddenBy } = stack.pop();
    const hasChildren = (node.children || []).length > 0;
    const isCollapsed = collapsed[node.id];
    out.push({ ...node, depth, hasChildren, hiddenBy });
    if (hasChildren && !isCollapsed && !hiddenBy) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push({ node: node.children[i], depth: depth + 1, hiddenBy: null });
      }
    }
  }
  return out.filter(x => !x.hiddenBy);
}

export default function OrgTree({ roots }) {
  const [collapsed, setCollapsed] = useState({});
  const flat = flatten(roots || [], collapsed);
  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div data-testid="org-tree-root">
      {flat.map(row => {
        const roleClass = ROLE_COLORS[row.teamRole] || ROLE_COLORS.fundraiser;
        const roleLabel = (row.teamRole || "fundraiser").replace(/_/g, " ");
        const isOpen = !collapsed[row.id];
        return (
          <div
            key={row.id}
            data-testid={`org-tree-node-${row.id}`}
            className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white transition-colors"
            style={{ paddingLeft: `${row.depth * 18 + 8}px` }}
          >
            {row.hasChildren ? (
              <button
                onClick={() => toggle(row.id)}
                className="w-4 h-4 flex items-center justify-center text-slate-400 hover:text-slate-700"
                data-testid={`org-tree-toggle-${row.id}`}
              >
                {isOpen ? "▾" : "▸"}
              </button>
            ) : (
              <span className="w-4 h-4 inline-block" />
            )}
            <span className="font-medium text-slate-900 text-sm">{row.name}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${roleClass}`}>
              {roleLabel}
            </span>
            <span className="text-xs text-slate-500 ml-auto flex items-center gap-3">
              <span title="Direct reports">DR: <b>{row.directReports}</b></span>
              <span title="All downstream">Down: <b>{row.downstreamTotal}</b></span>
              <span title="Active today" className="text-emerald-600">Active: <b>{row.activeToday}</b></span>
              <span title="No sync today" className="text-red-500">No-sync: <b>{row.noSyncToday}</b></span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
